import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CheckCircle, Clock, FileText, Users, Award, ArrowRight, Download, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { certificationProcess, evaluationRubrics } from '../data/mockData';

const Certification = () => {
  const [activeTab, setActiveTab] = useState('process');

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'pending':
        return <Clock className="h-6 w-6 text-yellow-500" />;
      default:
        return <Clock className="h-6 w-6 text-gray-300" />;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[500px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
            alt="Estudiantes en certificación"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50/65 to-white/75"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Award className="h-20 w-20 text-orange-600 mx-auto mb-6" />
            <h1 className="text-5xl font-bold mb-6 text-gray-900">
              Proceso de <span className="text-orange-600">Certificación</span>
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              Descubre cómo obtener tu certificación internacional en competencias
              para la docencia en línea con GRANA
            </p>
            <div className="inline-flex items-center bg-red-800 text-white px-6 py-3 rounded-lg font-semibold shadow-lg">
              <Users className="h-5 w-5 mr-2" />
              +2,500 docentes ya certificados
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('process')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'process'
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Proceso de Certificación
            </button>
            <button
              onClick={() => setActiveTab('rubrics')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'rubrics'
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Rubros de Evaluación
            </button>
            <button
              onClick={() => setActiveTab('requirements')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'requirements'
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Requisitos y Documentos
            </button>
          </nav>
        </div>
      </section>

      {/* Process Content */}
      {activeTab === 'process' && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Proceso Paso a Paso
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Un proceso transparente y accesible diseñado para docentes de todo el mundo
              </p>
            </div>

            <div className="space-y-8">
              {certificationProcess.map((step, index) => (
                <div key={step.step} className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center w-12 h-12 bg-amber-600 text-white rounded-full font-bold text-lg">
                      {step.step}
                    </div>
                  </div>
                  <div className="flex-1 bg-white p-6 rounded-lg shadow-md">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">
                          {step.title}
                        </h3>
                        <p className="text-gray-600 mb-4">
                          {step.description}
                        </p>
                        <div className="flex items-center space-x-4">
                          <span className="inline-flex items-center text-sm text-gray-500">
                            <Clock className="h-4 w-4 mr-1" />
                            {step.duration}
                          </span>
                          {step.status === 'available' && (
                            <button className="text-amber-600 hover:text-amber-700 font-medium text-sm">
                              Ver detalles →
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="ml-4">
                        {getStatusIcon(step.status)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center mt-12">
              <Link
                to="/contacto"
                className="bg-red-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-900 transition-colors duration-200 inline-flex items-center"
              >
                Iniciar mi Certificación Ahora
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Rubrics Content */}
      {activeTab === 'rubrics' && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Rubros de Evaluación Integral
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Cada docente es evaluado a través de 10 rubros que abarcan todas las dimensiones
                de la excelencia en la enseñanza digital
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {evaluationRubrics.map((rubric) => (
                <div key={rubric.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="bg-red-800 text-white p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-semibold">
                        {rubric.id}. {rubric.title}
                      </h3>
                      <span className="bg-amber-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {rubric.weight}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 mb-4">
                      {rubric.description}
                    </p>
                    <h4 className="font-semibold text-gray-800 mb-3">
                      Indicadores de evaluación:
                    </h4>
                    <ul className="space-y-2">
                      {rubric.indicators.map((indicator, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-600">{indicator}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <FileText className="h-6 w-6 text-amber-600" />
                <h3 className="text-lg font-semibold text-amber-800">
                  Documentación de Evidencias
                </h3>
              </div>
              <p className="text-amber-700 mb-4">
                Cada rubro debe ser respaldado por evidencias verificables, como documentos académicos,
                materiales didácticos, registros de investigación, productos digitales y participación
                en redes académicas.
              </p>
              <button className="bg-amber-600 text-white px-4 py-2 rounded-md hover:bg-amber-700 transition-colors duration-200 inline-flex items-center">
                <Download className="h-4 w-4 mr-2" />
                Descargar Guía de Evidencias
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Requirements Content */}
      {activeTab === 'requirements' && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Requisitos y Documentación
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Información detallada sobre los requisitos mínimos y documentos necesarios
                para iniciar tu proceso de certificación
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Requisitos Mínimos */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Requisitos Mínimos
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">
                      Título universitario de licenciatura mínimo
                    </span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">
                      Mínimo 2 años de experiencia docente en educación superior
                    </span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">
                      Experiencia demostrable en enseñanza virtual o híbrida
                    </span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">
                      Nivel intermedio de inglés (deseable pero no obligatorio)
                    </span>
                  </li>
                </ul>
              </div>

              {/* Documentos Requeridos */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Documentos Requeridos
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">
                      CV académico actualizado (formato PDF)
                    </span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">
                      Copias certificadas de títulos y diplomas
                    </span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">
                      Portafolio de evidencias pedagógicas digitales
                    </span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">
                      Cartas de recomendación institucionales (mínimo 2)
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Tutorial Video */}
            <div className="mt-12 bg-white rounded-lg shadow-md p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">
                    Tutorial: Cómo preparar tu documentación
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Aprende paso a paso cómo organizar y presentar tus evidencias
                    para maximizar tus posibilidades de éxito en la certificación.
                  </p>
                  <button className="bg-red-800 text-white px-6 py-3 rounded-lg hover:bg-red-900 transition-colors duration-200 inline-flex items-center">
                    <Play className="h-5 w-5 mr-2" />
                    Ver Tutorial Completo
                  </button>
                </div>
                <div className="bg-gray-200 rounded-lg h-64 flex items-center justify-center">
                  <Play className="h-16 w-16 text-gray-500" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default Certification;